import { useHistory } from "react-router-dom";

import { curWeight } from "../domain/energy";
import { foodOn } from "../domain/intake";
import { setupSteps } from "../domain/today";
import { useKaya } from "../store/kayaStore";
import { ActionBar } from "./ActionBar";
import { I } from "./icons";
import { NavRow, RowGroup } from "./Rows";
import { Segmented } from "./Segmented";
import { Sheet } from "./Sheet";

type Answer = "yes" | "no" | "unset";

/**
 * The first-run checklist behind Today's "Finish setting up" notice: body numbers, step counting,
 * whether there is a treadmill, and food logging. Each answer is read from `setupSteps`, so a
 * step done anywhere else in the app ticks off here too. "Done for now" puts the notice away on
 * this device (`LocalMeta.setupDismissedAt`), not in the synced state.
 */
export function SetupSheet({ open, onClose, ds }: { open: boolean; onClose: () => void; ds: string }) {
  const history = useHistory();
  const { S, meta, update, dismissSetup, answerSetupTreadmill } = useKaya();
  if (!open) return null;

  const steps = setupSteps(S, ds, { treadmillAnswered: meta.setupTreadmillAnswered });
  const done = (id: string) => steps.some((s) => s.id === id && s.done);
  const n = steps.filter((s) => s.done).length;
  const p = S.profile;

  const go = (to: string) => {
    onClose();
    history.push(to);
  };

  // Asked here because the default ("yes") says nothing about whether anyone has a treadmill: a
  // stored `true` counts as an answer only once this device has recorded one being given.
  const answer: Answer =
    p.hasTreadmill === false ? "no" : meta.setupTreadmillAnswered ? "yes" : "unset";
  const setTreadmill = (a: Answer) => {
    if (a === "unset") return;
    update((d) => void (d.profile.hasTreadmill = a === "yes"));
    answerSetupTreadmill();
  };

  const w = curWeight(S);
  const bodyLine = done("body")
    ? `Done · ${p.heightCm} cm${w ? ` · ${w} kg` : ""}${p.birthYear ? ` · born ${p.birthYear}` : ""}`
    : "Not yet · height, weight and birth year";

  return (
    <Sheet
      title="Finish setting up"
      sub={`${n} of ${steps.length} done · each takes under a minute`}
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          primary={{
            label: "Done for now",
            onClick: () => {
              dismissSetup();
              onClose();
            },
          }}
        />
      }
    >
      <RowGroup>
        <NavRow
          title="Body numbers"
          subtitle={bodyLine}
          icon={done("body") ? I.tick : I.user}
          tone={done("body") ? "success" : "default"}
          onClick={() => go("/you/body")}
        />
        <NavRow
          title="Step counting"
          subtitle={done("steps") ? "Done · your phone posted a count this week" : "Not yet · install Kaya Steps"}
          icon={done("steps") ? I.tick : I.footsteps}
          tone={done("steps") ? "success" : "default"}
          onClick={() => go("/you/steps")}
        />
        <NavRow
          kind="action"
          title="Do you have a treadmill?"
          subtitle={
            answer === "yes"
              ? "Done · the interval plan is on"
              : answer === "no"
                ? "Done · walks only"
                : done("treadmill")
                  ? "Done · a treadmill session is logged"
                  : "Not yet · sets up the interval plan"
          }
          icon={done("treadmill") ? I.tick : I.treadmill}
          tone={done("treadmill") ? "success" : "default"}
          action={
            <Segmented<Answer>
              ariaLabel="Do you have a treadmill?"
              value={answer}
              onChange={setTreadmill}
              options={[
                { value: "yes", label: "Yes" },
                { value: "no", label: "No" },
              ]}
            />
          }
        />
        <NavRow
          title="Food logging"
          subtitle={
            done("food")
              ? foodOn(S)
                ? "Done · you are logging food"
                : "Done · switched off"
              : "Not yet · set a daily budget"
          }
          icon={done("food") ? I.tick : I.bowl}
          tone={done("food") ? "success" : "default"}
          onClick={() => go("/you/food")}
        />
      </RowGroup>
    </Sheet>
  );
}
